// coveo-pagination.js
import { buildSearch as buildCommerceSearch, buildProductListing } from "https://static.cloud.coveo.com/shopify/v1/headless.esm.js";
import { initializeBindings } from "https://static.cloud.coveo.com/atomic/v3/index.esm.js";
import { buildPager } from "https://static.cloud.coveo.com/headless/v3/headless.esm.js";

// Maximum number of pages to display in the pagination UI.
// Any pages beyond this cap are hidden from navigation.
const MAX_PAGES = 25;

// --- 1. SHARED Helpers ---

// Returns a mixed sequence of page numbers and ellipsis markers.
function generatePaginationSequence(currentPageNumber, totalPages) {
  // Case 1: Small page sets show every page.
  // Example: totalPages=5 -> [1, 2, 3, 4, 5]
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  // Case 2: Near the start, avoid a leading ellipsis.
  // Example: page=1/2/3 of 100 -> [1, 2, 3, 4, "...", 100]
  if (currentPageNumber <= 3) {
    return [1, 2, 3, 4, "...", totalPages];
  }

  // Case 3: Near the end, avoid a trailing ellipsis.
  // Example: page=98/99/100 of 100 -> [1, "...", 97, 98, 99, 100]
  if (currentPageNumber >= totalPages - 2) {
    return [
      1,
      "...",
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  // Case 4: Middle window keeps current page centered.
  // Example: page=42 of 100 -> [1, "...", 41, 42, 43, "...", 100]
  return [
    1,
    "...",
    currentPageNumber - 1,
    currentPageNumber,
    currentPageNumber + 1,
    "...",
    totalPages,
  ];
}

// Maps a raw pagination sequence into the UI model consumed by pagination-ui.
// toValue lets wrappers adapt index conventions (e.g., commerce 0-based, search 1-based).
function mapSequenceToUiPages(rawSequence, currentPageNumber, toValue) {
  return rawSequence.map((item) => {
    if (item === "...") {
      return { label: "...", isEllipsis: true };
    }

    return {
      value: toValue(item),
      label: String(item),
      ariaPage: item,
      active: currentPageNumber === item,
      disabled: currentPageNumber === item,
    };
  });
}

// Waits for an Atomic interface engine to become available and returns it.
// Resolves null on timeout so wrappers can gracefully skip custom pagination.
function waitForInterfaceEngine(interfaceEl, timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (!interfaceEl) {
      resolve(null);
      return;
    }

    const getEngine = () => interfaceEl?.engine ?? null;

    const initialEngine = getEngine();
    if (initialEngine) {
      resolve(initialEngine);
      return;
    }

    const cleanup = () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };

    const observer = new MutationObserver(() => {
      const engine = getEngine();
      if (!engine) {
        return;
      }

      cleanup();
      resolve(engine);
    });

    observer.observe(interfaceEl, {
      attributes: true,
    });

    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
  });
}

// --- 2. BASE UI COMPONENT ---
class PaginationUi extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._state = {
      pages: [],
      hasPreviousPage: false,
      hasNextPage: false,
    };

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          all: initial;
          display: block;
        }

        nav {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: center;
          gap: 0;
          padding: 0;
          width: 100%;
          min-height: 48px;
        }

        button {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          min-width: 40px;
          height: 40px;
          padding: 0;
          border: 0;
          border-radius: 0;
          box-shadow: none;
          background: transparent;
          color: #323232;
          font-family: var(--family-Roboto, Roboto);
          font-size: var(--size-size-16, 16px);
          font-weight: 700;
          line-height: 24px; 
          letter-spacing: 0.16px;
          text-align: center;
          cursor: pointer;
        }
        
        @media screen and (max-width: 375px) {
          button {
            width: 36px;
            min-width: 36px;
            height: 36px;
          }
        }
        
        button:focus-visible {
          outline: 2px solid #085fcc;
        }

        button:not(:disabled):hover {
          background: transparent;
        }

        button.active {
          font-weight: 700;
        }

        button.active::after {
          content: "";
          position: absolute;
          left: 50%;
          width: 40px;
          bottom: 0;
          height: 0;
          transform: translateX(-50%);
          border-bottom: 2px solid currentColor;
        }

        button:disabled {
          cursor: default;
          color: #323232;
          opacity: 1;
        }

        .ellipsis {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          min-width: 48px;
          height: 48px;
          color: var(--text-black);
          font-family: var(--font-family-base);
          font-size: 14px;
          line-height: 18px;
          letter-spacing: 2px;
        }

        .icon {
          width: 24px;
          height: 24px;
          font-size: 34px;
          line-height: 0.8;
          font-weight: 300;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        #pagination-nav{
          gap:6px;
          margin-top:24px;
          align-items: center;
        }

        @media screen and (max-width: 449.98px) {
          #pagination-nav{
            gap: 4px;
            max-width: calc(100vw - 32px);
          };
        }

        @media screen and (max-width: 374.98px) {
          #pagination-nav{
            gap: 2px;
          };
        }

      </style>
      <nav id="pagination-nav"></nav>
    `;
  }

  set state(newState) {
    const {
      pages = [],
      hasPreviousPage = false,
      hasNextPage = false,
    } = newState ?? {};

    this._state = {
      pages: Array.isArray(pages) ? pages : [],
      hasPreviousPage: Boolean(hasPreviousPage),
      hasNextPage: Boolean(hasNextPage),
    };
    this.render();
  }

  get state() {
    return this._state;
  }

  connectedCallback() {
    this.shadowRoot.addEventListener("click", this.onNavClick);
  }

  disconnectedCallback() {
    this.shadowRoot.removeEventListener("click", this.onNavClick);
  }

  onPreviousClick = () => {
    this.dispatchEvent(
      new CustomEvent("pagination-previous", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  onNextClick = () => {
    this.dispatchEvent(
      new CustomEvent("pagination-next", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  onSelectPage = (value) => {
    this.dispatchEvent(
      new CustomEvent("pagination-select", {
        detail: { value },
        bubbles: true,
        composed: true,
      }),
    );
  };

  pageMarkup = (page, index, total) => {
    if (page.isEllipsis) {
      return '<span class="ellipsis" aria-hidden="true">...</span>';
    }

    const classes = page.active ? ' class="active"' : "";
    const ariaCurrent = page.active ? ' aria-current="page"' : "";
    const isFirstPage = index === 0;
    const firstPageLabel = isFirstPage ? "First page, " : "";
    const isLastPage = index === total - 1;
    const lastPageLabel = isLastPage ? "Last page, " : "";
    const ariaLabel = page.ariaPage != null ? ` aria-label="${firstPageLabel}${lastPageLabel}Page ${page.ariaPage}"` : "";
    // const disabled = page.disabled ? " disabled" : "";

    return `<button type="button"${classes}${ariaCurrent}${ariaLabel} data-action="select" data-page="${String(page.value)}">${page.label ?? ""}</button>`;
  };

  onNavClick = (event) => {
    const target = event.target.closest("button[data-action]");
    if (!target) {
      return;
    }

    const action = target.dataset.action;
    if (action === "previous") {
      this.onPreviousClick();
      return;
    }

    if (action === "next") {
      this.onNextClick();
      return;
    }

    if (action === "select") {
      const pageValue = Number(target.dataset.page);
      if (!Number.isNaN(pageValue)) {
        this.onSelectPage(pageValue);
      }
    }
  };

  render = () => {
    if (!this.shadowRoot) {
      return;
    }

    const nav = this.shadowRoot.querySelector("#pagination-nav");
    if (!nav) {
      return;
    }

    const { pages, hasPreviousPage, hasNextPage } = this._state;

    if (!pages.length) {
      nav.innerHTML = "";
      return;
    }


    const previousMarkup = hasPreviousPage
      ? '<button type="button" aria-label="Previous page" data-action="previous"><span class="icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="17" viewBox="0 0 11 17" fill="none"><path d="M9.46191 0.729492L1.46191 8.22949L9.46191 15.7295" stroke="#323232" stroke-width="2"/></svg></span></button>'
      : '<button type="button" aria-label="Previous page" disabled data-action="previous"><span class="icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="17" viewBox="0 0 11 17" fill="none"><path d="M9.46191 0.729492L1.46191 8.22949L9.46191 15.7295" stroke="#ADADAD" stroke-width="2"/></svg></span></button>';

    const nextMarkup = hasNextPage
      ? '<button type="button" aria-label="Next page" data-action="next"><span class="icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="17" viewBox="0 0 11 17" fill="none"><path d="M0.683594 15.7295L8.68359 8.22949L0.683594 0.729492" stroke="#323232" stroke-width="2"/></svg></span></button>'
      : '<button type="button" aria-label="Next page" disabled data-action="next"><span class="icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="17" viewBox="0 0 11 17" fill="none"><path d="M0.683594 15.7295L8.68359 8.22949L0.683594 0.729492" stroke="#ADADAD" stroke-width="2"/></svg></span></button>';

    const pagesMarkup = pages.map((page,index, arr) => this.pageMarkup(page,index, arr.length)).join("");

    nav.innerHTML = `
      ${previousMarkup}
      ${pagesMarkup}
      ${nextMarkup}
    `;
  };
}

customElements.define("pagination-ui", PaginationUi);

// --- 3. COMMERCE WRAPPER ---

class PaginationCommerce extends HTMLElement {
  constructor() {
    super();
    this.pagination = null;
    this.unsubscribePagination = null;
    this.ui = null;
  }

  async connectedCallback() {
    this.addEventListener("pagination-previous", this.handlePrevious);
    this.addEventListener("pagination-next", this.handleNext);
    this.addEventListener("pagination-select", this.handleSelect);

    await this.initializeController();
  }

  disconnectedCallback() {
    this.removeEventListener("pagination-previous", this.handlePrevious);
    this.removeEventListener("pagination-next", this.handleNext);
    this.removeEventListener("pagination-select", this.handleSelect);

    if (this.unsubscribePagination) {
      this.unsubscribePagination();
      this.unsubscribePagination = null;
    }
  }

  async initializeController() {
    if (this.pagination) {
      return;
    }

    const interfaceTag = 'atomic-commerce-interface';
    await customElements.whenDefined(interfaceTag);
    const interfaceEl = this.closest(interfaceTag) ?? document.querySelector(interfaceTag);

    if (!interfaceEl) return;

    const engine = await waitForInterfaceEngine(interfaceEl);
    if (!engine) {
      console.warn("[pagination-commerce] Engine was not ready in time. Hiding custom pagination.");
      this.innerHTML = "";
      this.ui = null;
      return;
    }

    try {
      const interfaceType = interfaceEl.getAttribute('type');

      if (interfaceType === 'search') {
        this.pagination = buildCommerceSearch(engine).pagination();
      } else {
        this.pagination = buildProductListing(engine).pagination();
      }

      this.unsubscribePagination = this.pagination.subscribe(this.render);
      this.render();
    } catch (error) {
      console.warn("[pagination-commerce] Failed to initialize custom pagination.", error);
      this.innerHTML = "";
      this.ui = null;
    }
  }

  handlePrevious = () => {
    this.pagination?.previousPage();
    this.scrollToTop();
  };

  handleNext = () => {
    this.pagination?.nextPage();
    this.scrollToTop();
  };

  handleSelect = (event) => {
    const page = event?.detail?.value;
    if (typeof page !== "number") {
      return;
    }
    this.pagination?.selectPage(page);
    this.scrollToTop();
  };

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  buildPages() {
    const { page: currentPage, totalPages } = this.pagination.state;
    const cappedTotalPages = Math.min(totalPages, MAX_PAGES);
    const currentPageNumber = currentPage + 1;
    const rawSequence = generatePaginationSequence(currentPageNumber, cappedTotalPages);

    return mapSequenceToUiPages(rawSequence, currentPageNumber, (item) => item - 1);
  }

  render = () => {
    if (!this.pagination) {
      this.innerHTML = "";
      this.ui = null;
      return;
    }

    const { page: currentPage, totalPages } = this.pagination.state;
    const cappedTotalPages = Math.min(totalPages, MAX_PAGES);

    if (!cappedTotalPages || cappedTotalPages < 1) {
      if (this.ui) {
        this.ui.state = {
          pages: [],
          hasPreviousPage: false,
          hasNextPage: false,
        };
      }
      return;
    }

    const pages = this.buildPages();

    if (!this.ui) {
      this.innerHTML = "";
      this.ui = document.createElement("pagination-ui");
      this.appendChild(this.ui);
    }

    this.ui.state = {
      pages,
      hasPreviousPage: currentPage > 0,
      // Commerce uses 0-based indexing; page index 24 is the 25th (last) page.
      hasNextPage: currentPage < cappedTotalPages - 1,
    };
  };
}

customElements.define("pagination-commerce", PaginationCommerce);

// --- 4. SEARCH WRAPPER ---

class PaginationSearch extends HTMLElement {
  constructor() {
    super();
    this.pager = null;
    this.unsubscribePager = null;
    this.ui = null;
  }

  async connectedCallback() {
    this.addEventListener("pagination-previous", this.handlePrevious);
    this.addEventListener("pagination-next", this.handleNext);
    this.addEventListener("pagination-select", this.handleSelect);

    await this.initializeController();
  }

  disconnectedCallback() {
    this.removeEventListener("pagination-previous", this.handlePrevious);
    this.removeEventListener("pagination-next", this.handleNext);
    this.removeEventListener("pagination-select", this.handleSelect);

    if (this.unsubscribePager) {
      this.unsubscribePager();
      this.unsubscribePager = null;
    }
  }

  async initializeController() {
     if (this.pager) {
      return;
    }

    const interfaceTag = 'atomic-search-interface';
    await customElements.whenDefined(interfaceTag);
    const interfaceEl = this.closest(interfaceTag) ?? document.querySelector(interfaceTag);

    if (!interfaceEl) return;

    

    const engine = await waitForInterfaceEngine(interfaceEl);
    if (!engine) {
      console.warn("[pagination-search] Engine was not ready in time. Hiding custom pagination.");
      this.innerHTML = "";
      this.ui = null;
      return;
    }

    try {
      this.pager = buildPager(engine);
      this.unsubscribePager = this.pager.subscribe(this.render);
      this.render();
    } catch (error) {
      console.warn("[pagination-search] Failed to initialize custom pagination.", error);
      this.innerHTML = "";
      this.ui = null;
    }
  }

  handlePrevious = () => {
    this.pager?.previousPage();
    this.scrollToTop();
  };

  handleNext = () => {
    this.pager?.nextPage();
    this.scrollToTop();
  };

  handleSelect = (event) => {
    const page = event?.detail?.value;
    if (typeof page !== "number") {
      return;
    }
    this.pager?.selectPage(page);
    this.scrollToTop();
  };

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  buildPages(totalPages, currentPage) {
    const rawSequence = generatePaginationSequence(currentPage, totalPages);

    return mapSequenceToUiPages(rawSequence, currentPage, (item) => item);
  }

  render = () => {
    if (!this.pager) {
      this.innerHTML = "";
      this.ui = null;
      return;
    }

    const {
      currentPage,
      hasNextPage,
      hasPreviousPage,
      maxPage,
    } = this.pager.state;

    const pageCount = Math.min(Number(maxPage) || 0, MAX_PAGES);

    if (!pageCount) {
      if (this.ui) {
        this.ui.state = {
          pages: [],
          hasPreviousPage: false,
          hasNextPage: false,
        };
      }
      return;
    }

    const pages = this.buildPages(pageCount, currentPage);

    if (!this.ui) {
      this.innerHTML = "";
      this.ui = document.createElement("pagination-ui");
      this.appendChild(this.ui);
    }

    this.ui.state = {
      pages,
      hasPreviousPage,
      // Search uses 1-based indexing; page 25 is the last page.
      hasNextPage: hasNextPage && currentPage < MAX_PAGES,
    };
  };
}

customElements.define("pagination-search", PaginationSearch);